/**
 * Issue types of the view (bookmark) validation. The server-side validation
 * (server/api/utility/bookmark-validation.js) produces most of them, the
 * client adds the access checks (workspace-restricted-*) for the current user.
 */
export type BookmarkValidationIssueType =
  | 'unpublished-data'
  | 'unpublished-indicator'
  | 'unpublished-datalayer'
  | 'unpublished-reference'
  | 'missing-indicator'
  | 'missing-datalayer'
  | 'missing-reference'
  | 'deleted-indicator'
  | 'deleted-datalayer'
  | 'deleted-reference'
  | 'foreign-commune-reference'
  | 'shy-workspace-indicator'
  | 'workspace-restricted-datalayer'
  | 'workspace-restricted-indicator'
  | 'permission-denied-data'
  | 'no-data-configured'
  | 'no-data-available';

/** Who will see a view - decides which issues count (see server validator). */
export type BookmarkAudience = 'public' | 'guest' | 'private' | 'internal';

export interface BookmarkValidationIssue {
  bookmarkId: string;
  bookmarkTitle: string;
  issueType: BookmarkValidationIssueType;
  /** set by the server validation */
  severity?: 'danger' | 'warning' | 'info';
  widgetId: string;
  widgetType: string;
  widgetHeadline?: string;
  details: {
    dataLayerId?: string;
    dataLayerIds?: string[];
    dataLayerName?: string;
    indicatorId?: string;
    indicatorIds?: string[];
    indicatorName?: string;
    indicatorDataId?: string;
    workspaceName?: string; // For workspace restriction issues
    requiredWorkspace?: string; // ID of required workspace
    // other references (colorset, topic, story, image, bookmark, ...)
    referenceType?: string;
    referenceId?: string;
    referenceName?: string;
    /** an unpublished topic the view is filed under (not a widget's link) */
    topicMembership?: boolean;
  };
  message: string;
  causedByAccessIssue?: boolean;
}

export interface BookmarkValidationResult {
  bookmarkId: string;
  bookmarkTitle: string;
  audience?: BookmarkAudience;
  isValid: boolean;
  /** only warnings left (see `isWarningIssue`) - valid, but worth a look */
  hasWarnings?: boolean;
  issues: BookmarkValidationIssue[];
  validatedAt: Date;
}

/**
 * Issues that don't make a view faulty, only worth a warning: an unpublished
 * topic the view is filed under (the view itself still works, it is just not
 * listed there in the public atlas).
 */
export function isWarningIssue(issue: BookmarkValidationIssue): boolean {
  return !!issue.details.topicMembership;
}

export interface BookmarkValidationSummary {
  totalBookmarks: number;
  validBookmarks: number;
  invalidBookmarks: number;
  /** valid views that only have warnings */
  warningBookmarks?: number;
  totalIssues: number;
  results: BookmarkValidationResult[];
  validatedAt: Date;
  // Access validation (workspace/permission issues)
  accessValidation?: {
    totalBookmarks: number;
    validBookmarks: number;
    invalidBookmarks: number;
    totalIssues: number;
    results: BookmarkValidationResult[];
  };
  // Data availability validation (checks if widgets have data)
  dataValidation?: {
    totalBookmarks: number;
    validBookmarks: number;
    invalidBookmarks: number;
    totalIssues: number;
    results: BookmarkValidationResult[];
  };
}

/**
 * Grouped issues by unique identifier
 * Used to display multiple widgets that share the same underlying problem
 */
export interface GroupedIssue {
  groupKey: string; // Unique identifier for the group
  causedByAccessIssue?: boolean; // True if all issues in the group are caused by access restrictions
  issueType: BookmarkValidationIssueType;
  issues: BookmarkValidationIssue[]; // All issues in this group
  displayName: string; // Human-readable name for the group
  details: BookmarkValidationIssue['details'];
  message: string;
}
